import { useLayoutEffect, useState } from 'react';
import { breakpointsPx } from '../theme/theme';

const device = {
	desktopLarge: `(min-width: ${breakpointsPx.xl}px)`,
	desktop: `(min-width: ${breakpointsPx.lg}px) and (max-width: ${breakpointsPx.xl - 1}px)`,
	tablet: `(min-width: ${breakpointsPx.md}px) and (max-width: ${breakpointsPx.lg - 1}px)`,
	mobile: `(max-width: ${breakpointsPx.md - 1}px)`,
};

type TDeviceSize = keyof typeof device;

const useDevice = () => {
	const [currentDevice, setDevice] = useState<TDeviceSize | null>(null);

	useLayoutEffect(() => {
		const getDeviceFromWidth = (): TDeviceSize => {
			if (window.innerWidth >= breakpointsPx.xl) return 'desktopLarge';
			if (window.innerWidth >= breakpointsPx.lg) return 'desktop';
			if (window.innerWidth >= breakpointsPx.md) return 'tablet';
			return 'mobile';
		};

		const handleChangeListener = (device: TDeviceSize) => {
			return (event: MediaQueryListEvent) => {
				if (event.matches) {
					setDevice(device);
				}
			};
		};

		// Bind the handler once per query so the cleanup can actually detach it. Building a fresh
		// handler in the cleanup made removeEventListener a no-op and leaked a listener per mount.
		const mediaQueries = Object.keys(device).map(key => ({
			device: key as TDeviceSize,
			query: window.matchMedia(device[key]),
			handler: handleChangeListener(key as TDeviceSize),
		}));
		const matchingDevice = mediaQueries.find(deviceQueryMap => deviceQueryMap.query.matches)?.device;

		setDevice(matchingDevice || getDeviceFromWidth());

		// Safari only gained MediaQueryList.addEventListener in 14; older versions expose the
		// deprecated addListener instead.
		mediaQueries.forEach(({ query, handler }) => {
			if (typeof query.addEventListener === 'function') query.addEventListener('change', handler);
			else if (typeof query.addListener === 'function') query.addListener(handler);
		});

		return () => {
			mediaQueries.forEach(({ query, handler }) => {
				if (typeof query.removeEventListener === 'function') query.removeEventListener('change', handler);
				else if (typeof query.removeListener === 'function') query.removeListener(handler);
			});
		};
	}, []);

	const largeDevice = currentDevice === 'desktopLarge';
	const desktopDevice = currentDevice === 'desktop';
	const smallDevice = currentDevice === 'tablet' || currentDevice === 'mobile';
	const mobileDevice = currentDevice === 'mobile';

	return {
		currentDevice,
		largeDevice,
		desktopDevice,
		smallDevice,
		mobileDevice,
	};
};

export { device, useDevice };
